import {
    Injectable,
    inject
} from '@angular/core';

import {
    ConfigurationManagerService,
    EntityManagerService,
    PrinterDocumentType,
    PrinterDocumentTypeId
} from 'src/app/core';

import {TranslateService} from '@ngx-translate/core';

import {
    AlertDialogService,
    ConfirmDialogService,
    ListSelectorDialogService,
    PrinterErrorFiscalDialogService,
    PromptDialogService,
    RefundItemsSelectorDialogService,
    SaleDocumentsViewerDialogService,
    WaitDialogService,
    WaitDialogModel
} from 'src/app/dialogs';

import {
    documentPrinter,
    FiscalPrinters,
    FiscalProviders,
    fiscalUtils,
    optionsDialog,
    restManager,
} from 'app/ajs-upgraded-providers';

import {
    Printers,
    Sales,
    SalesCustomer,
    SalesDocuments,
    SalesItems,
    SalesItemsPriceChanges,
    SalesPayments,
    SalesPriceChanges
} from 'tilby-models';

import {GridServerSideComponent} from '../../../../shared/components/grid-server-side/grid-server-side.component';
import {HistoryUtilsService} from './history-utils.service';
import { TilbyCurrencyPipe } from '@tilby/tilby-ui-lib/pipes/tilby-currency';
import { REFUND_CAUSES, RefundCauses } from 'src/app/core/constants/refund-causes';
import { ExpenseReportDialogService } from 'src/app/dialogs/history/expense-report-dialog/expense-report-dialog.component';
import {
    CustomerUtilsService,
    SaleBatchPrintService,
    SalePaymentService,
    SaleUtilsService
} from 'src/app/features';
import { DocumentPrinterOptions } from 'src/app/shared/model/document-printer.model';
import {
    BehaviorSubject,
    Subject
} from 'rxjs';

import {
    asyncParallelLimit,
    chunk,
    generateUuid,
    MathUtils
} from 'src/app/shared/utils';

type CreateSaleOptions = {
    addInvoice?: boolean;
    returnOnly?: boolean;
}

type SaleReference = {
    reference_sale_id: number;
    reference_sequential_number?: number;
    reference_date?: Date;
    reference_text?: string;
}

type RefundCauseWithName = RefundCauses & {
    name: string;
}

@Injectable({
    providedIn: 'root'
})
export class HistorySalesService {

    private printInProgress = false;

    // public printInProgressChange: Subject<boolean> = new Subject<boolean>();

    // Subject per monitorare lo stato dello spinner e la percentuale
    public loadingSubject = new BehaviorSubject<boolean>(false);
    public progressSubject = new BehaviorSubject<number>(0);

    selectedSales: Sales[] = [];

    private readonly alertDialogService = inject(AlertDialogService);
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly confirmDialogService = inject(ConfirmDialogService);
    private readonly documentPrinterService = inject(documentPrinter);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly expenseReportDialogService = inject(ExpenseReportDialogService);
    private readonly FiscalPrintersService = inject(FiscalPrinters);
    private readonly FiscalProvidersService = inject(FiscalProviders);
    private readonly fiscalUtilsService = inject(fiscalUtils);
    private readonly historyUtilsService = inject(HistoryUtilsService);
    private readonly optionsDialogService = inject(optionsDialog);
    private readonly printerErrorFiscalService = inject(PrinterErrorFiscalDialogService);
    private readonly listSelectorDialogService = inject(ListSelectorDialogService);
    private readonly refundItemsSelectorDialogService = inject(RefundItemsSelectorDialogService);
    private readonly saleBatchPrintService = inject(SaleBatchPrintService);
    private readonly saleDocumentsViewerDialogService = inject(SaleDocumentsViewerDialogService);
    private readonly salePaymentService = inject(SalePaymentService);
    private readonly saleUtilsService = inject(SaleUtilsService);
    private readonly tilbyCurrencyPipe = inject(TilbyCurrencyPipe);
    private readonly translate = inject(TranslateService);
    private readonly waitDialogService = inject(WaitDialogService);
    private readonly promptDialogService = inject(PromptDialogService);
    private readonly restManagerService = inject(restManager);

    isEInvoice(sale: Sales) {return sale?.e_invoice?.invoice_progressive;}

    isCreditNote(sale: Sales) { return sale?.final_amount && sale?.final_amount < 0;};

    private reversePriceChange = (priceChange: SalesPriceChanges | SalesItemsPriceChanges) => {
        switch(priceChange.type) {
            case 'discount_fix':
                priceChange.type = 'surcharge_fix';
                break;
            case 'surcharge_fix':
                priceChange.type = 'discount_fix';
                break;
        }
    }

    private async createReverseSale(targetSale: Sales, saleType: any, refundCause: RefundCauses, saleCustomer?: SalesCustomer | string, options?: CreateSaleOptions) {
        const flattenPriceChanges = !['void_doc', 'e_credit_note'].includes(saleType);
        const firstDoc = targetSale.sale_documents?.[0];
        const refSeqNum = firstDoc?.sequential_number;
        const refDate = firstDoc?.date;

        let saleItems = (targetSale.sale_items || [])
            .filter(saleItem => !['refund', 'gift'].includes(saleItem.type) && !this.fiscalUtilsService.isTipSaleItem(saleItem))
            .map(saleItem => structuredClone(saleItem));

        if (!['void_doc', 'e_credit_note'].includes(saleType)) {
            saleItems = await this.refundItemsSelectorDialogService.openDialog({ data: { saleItems: saleItems } });

            if (!saleItems.length) {
                return;
            }
        }

        for (const saleItem of saleItems) {
            Object.assign(saleItem, {
                quantity: -saleItem.quantity,
                type: "refund",
                refund_cause_id: refundCause.id,
                refund_cause_description: this.translate.instant(refundCause.translation_id),
                reference_sequential_number: refSeqNum,
                reference_date: refDate
            });

            for(const priceChange of saleItem.price_changes ?? []) {
                this.reversePriceChange(priceChange);
            }
        }

        for(const priceChange of targetSale.price_changes ?? []) {
            this.reversePriceChange(priceChange);
        }

        const salePayments: SalesPayments[] = (targetSale.payments || [])
            .filter(p => !p.is_tip)
            .map((payment) => ({
                amount: -payment.amount,
                date: new Date().toISOString() as unknown as Date,
                payment_method_id: payment.payment_method_id,
                payment_method_name: payment.payment_method_name,
                payment_method_type_id: payment.payment_method_type_id,
                payment_method_type_name: payment.payment_method_type_name,
                payment_condition_code: payment.payment_condition_code,
                payment_data: [33].includes(payment.payment_method_type_id) ? payment.payment_data : undefined
            }));

        const saleChange = targetSale.change || 0;

        // Remove change from payments
        if(saleChange) {
            let targetPayment: SalesPayments | undefined;

            switch(targetSale.change_type) {
                case "cash":
                    targetPayment = salePayments.find((payment) => (
                        this.fiscalUtilsService.isCashPayment(payment.payment_method_type_id) &&
                        Math.abs(payment.amount) >= saleChange
                    ));
                    break;
            }

            if(targetPayment) {
                targetPayment.amount = MathUtils.round(targetPayment.amount + saleChange);
            }
        }

        return this.historyUtilsService.createSale({
            e_invoice: options?.addInvoice ? targetSale.e_invoice : undefined,
            name: targetSale.name,
            sale_items: saleItems,
            sale_customer: saleCustomer,
            price_changes: flattenPriceChanges ? undefined : (targetSale.price_changes || []),
            payments: salePayments,
            overrides: {
                sale_parent_id: targetSale.id,
                sale_parent_uuid: targetSale.uuid
            }
        }, {
            saleType: saleType,
            returnOnly: !!options?.returnOnly
        });
    };

    private async doRefundVoid(sale: Sales, options?: { refundCauseId?: number, gridRef?: GridServerSideComponent }) {
        if (sale?.status !== 'closed') {
            return;
        }

        if (!this.configurationManagerService.isModuleEnabled('cashregister')) {
            throw 'CASHREGISTER_MODULE_NOT_ENABLED';
        }

        //Ask refund cause to the user
        const refCauses: RefundCauseWithName[] = REFUND_CAUSES.map(cause => ({
            ...cause,
            name: this.translate.instant(cause.translation_id)
        }));

        let refundCause;

        if (options?.refundCauseId) {
            refundCause = refCauses.find(cause => cause.id === options?.refundCauseId);
        }

        if (!refundCause) {
            refundCause = await this.listSelectorDialogService.openDialog<RefundCauseWithName>({
                data: {
                    optionList: refCauses,
                    title: 'HISTORY.SALE.REASON',
                    searchable: false
                }
            });
        }

        if (typeof refundCause?.id !== 'number') {
            return;
        }

        // Don't allow refund if document sending to ZPMS is enabled or the sale has a ZPMS payment
        let usesPms = this.configurationManagerService.getPreference('zucchetti_pms.send_other_sales') || sale.payments?.some(payment => [36, 43].includes(payment.payment_method_type_id));

        //Handle PMS cases
        if (usesPms && refundCause.id !== 6) {
            throw 'SALE.UNALLOWED_OPERATION';
        }

        const targetSale = structuredClone(sale);
        const saleCustomer = targetSale.sale_customer ? structuredClone(targetSale.sale_customer) : targetSale.customer_tax_code;

        if (this.isEInvoice(sale)) {
            return this.createReverseSale(targetSale, 'e_credit_note', refundCause, saleCustomer, { addInvoice: true, returnOnly: !!options?.refundCauseId });
        }

        const printers = await this.entityManagerService.printers.fetchCollectionOffline();

        //Use dedicated routine if we are dealing with a fiscal provider
        const fiscalProviderDocument = targetSale.sale_documents?.find((doc) => doc.document_type === 'fiscal_provider');

        if (fiscalProviderDocument) {
            await this.voidFiscalProviderDocument(targetSale, fiscalProviderDocument, printers, refundCause, saleCustomer, options?.gridRef);
            return;
        }

        //Deal with all other cases
        const rtPrinters = printers.filter((printer) => printer.type === 'rt');
        const hasRTPrinter = !!printers.length
        const docType = targetSale.sale_documents?.[0]?.document_type;

        switch (docType) {
            case 'commercial_doc':
                const printerSerial = targetSale.sale_documents?.[0]?.printer_serial;

                //We cannot void a commercial document if we don't have the printer serial or a configured RT printer
                if (refundCause.id === 6 && (!hasRTPrinter || !printerSerial)) {
                    throw 'SALE.CANNOT_CANCEL_DOCUMENT';
                }

                switch (refundCause.id) {
                    case 6: //VOID
                        if ((targetSale.sale_items || []).every((si) => si.price === 0)) {
                            throw 'SALE.CANNOT_CANCEL_ZERO_RECEIPT';
                        }

                        return this.voidRTCommercialDocument(targetSale, rtPrinters, printerSerial || '', refundCause, saleCustomer, options?.gridRef);
                    default: //Refund
                        return this.createReverseSale(targetSale, 'refund_doc', refundCause, saleCustomer);
                }
                break;
            case 'fiscal_receipt':
            case 'invoice':
            case 'receipt_invoice':
            case 'summary_invoice':
            case 'summary_e_rc':
            case 'summary_e_nrc':
            case 'shipping_invoice':
                if (hasRTPrinter && !this.configurationManagerService.getPreference('history.override_credit_note_check')) {
                    throw 'SALE.CANNOT_CANCEL_DOCUMENT';
                }

                return this.createReverseSale(targetSale, 'credit_note', refundCause, saleCustomer);
            case 'generic_receipt':
            case 'generic_invoice':
            case 'generic_document':
                if ((refundCause.id === 6 && usesPms) || options?.refundCauseId === 6) { // If voiding with a PMS or we are doing a non-interactive void
                    await this.emitNonFiscalVoidSale(targetSale, refundCause, saleCustomer, undefined, undefined, options?.gridRef);
                } else {
                    return this.createReverseSale(targetSale, docType, refundCause, saleCustomer, { returnOnly: !!options?.refundCauseId });
                }
            default:
                break;
        }
    }

    private async showError(err: any) {
        switch (typeof err) {
            case 'string': {
                let translateOptions;

                if (err === 'SALE.CANNOT_CANCEL_ZERO_RECEIPT') {
                    translateOptions = { value: this.tilbyCurrencyPipe.transform(0, undefined, 0) };
                }

                const errCorrected = err.startsWith('CASHREGISTER.')
                    ? err
                    : `HISTORY.${err}`;

                if (errCorrected === 'CASHREGISTER.ACTIVE_SALE.FISCAL_PROVIDER_DOCUMENT_OPEN_FAILED') {
                    return false;
                }

                await this.alertDialogService.openDialog({
                    data: {
                        messageLabel: this.translate.instant(errCorrected, translateOptions)
                    }
                });

                return true;
            }
            case 'object': {
                if ('printError' in err && 'printerId' in err) {
                    await this.printerErrorFiscalService.openDialog({
                        data: {
                            error: err.printError,
                            options: { printerId: err.printerId as number }
                        }
                    });

                    return true;
                } else {
                    return false;
                }
            }
            default: {
                return false;
            }
        }
    }

    public async refundVoid(sale: Sales, options?: { refundCauseId?: number, gridRef?: GridServerSideComponent }) {
        try {
            return await this.doRefundVoid(sale, options);
        } catch (err) {
            await this.showError(err);
        }
    }

    public async createExpenseReport(sale: Sales, templateId: number, gridRef?: GridServerSideComponent) {
        const template = await this.entityManagerService.nonfiscalDocuments.fetchOneOffline(templateId);

        if(!template) {
            //TODO: show error
            return;
        }

        const printerId = sale.sale_documents?.[0]?.printer_id;

        if(!printerId) {
            //TODO: show error
            return;
        }

        const targetPrinter = await this.entityManagerService.printers.fetchOneOffline(printerId);

        if(!targetPrinter) {
            //TODO: show error
            return;
        }

        const res = await this.expenseReportDialogService.openDialog({data: { sale: sale }});

        if(!res) {
            return;
        }

        const expenseReportSale = await this.saleUtilsService.getSaleTemplate();
        expenseReportSale.sale_items = await this.saleUtilsService.repartitionSaleItemsByDepartment(sale, 'by_covers', res.covers, undefined, res.amount, { singleQuantity: true });

        for(const item of expenseReportSale.sale_items) {
            item.name = `${res.covers}x ${this.translate.instant('HISTORY.EXPENSE_REPORT.SALE_ITEM_NAME')}`;
        }

        this.saleUtilsService.calculateSalePrices(expenseReportSale);

        const printingConfig: DocumentPrinterOptions = {
            printer: targetPrinter,
            document_type: { id: 'expense_report' },
            document_template: template,
            options: {}
        }

        await this.documentPrinterService.printDocument(expenseReportSale, printingConfig);

        this.entityManagerService.sales.postOneOfflineFirst({
            ...expenseReportSale,
            //@ts-expect-error
            closed_at: new Date().toISOString(),
            sale_parent_uuid: sale.uuid,
            status: 'closed'
        });

        await new Promise(resolve => setTimeout(resolve, 1000));

        gridRef?.refreshGrid();
    }

    public async printCourtesyReceipt(sale: Sales) {
        if (this.printInProgress) {
            return;
        }

        try {
            if (!this.configurationManagerService.isModuleEnabled('cashregister')) {
                throw 'NO_CASHREGISTER_MODULE';
            }

            const printerDefId = parseInt(this.configurationManagerService.getPreference('fiscalprinter.def.id') || '0');

            if (!printerDefId) {
                throw 'NO_DEFAULT_PRINTER';
            }

            this.printInProgress = true;

            try {
                await this.waitDialogService.openDialog({
                    data: {
                        message: 'HISTORY.SALE.PRINT_IN_PROGRESS',
                        promise: this.FiscalPrintersService.printCourtesyReceipt(sale, printerDefId)
                    }
                });
            } catch(err) {
                this.printerErrorFiscalService.show(err, { printerId: printerDefId });
            } finally {
                this.printInProgress = false;
            }
        } catch (err) { //Catches configuration/permission errors
            await this.alertDialogService.openDialog({data: {messageLabel: `HISTORY.SALE.${err}`}});
        }
    };

    public async showSaleDocuments(sale: Sales) {
        await this.saleDocumentsViewerDialogService.openDialog({
            data: {
                sale: sale
            }
        });
    }

    private getEmittedDocument(targetSale?: Sales) {
        const documentsToIgnore = new Set([
            'attachment',
            'commercial_doc_signed',
            'expense_report',
            'fiscal_provider',
            'receipt',
        ]);

        return targetSale?.sale_documents?.find((saleDocument) => !documentsToIgnore.has(saleDocument.document_type));
    }

    private async emitNonFiscalVoidSale(targetSale: Sales, refundCause: RefundCauses, saleCustomer?: SalesCustomer | string, printer?: Printers, refDocument?: SalesDocuments, gridRef?: GridServerSideComponent) {
        const nonFiscalDocument = this.getEmittedDocument(targetSale);

        if (!nonFiscalDocument) {
            throw 'SALE.CANNOT_CANCEL_DOCUMENT';
        }

        const receiptDocument = await this.entityManagerService.nonfiscalDocuments.fetchCollectionOffline({type: nonFiscalDocument?.document_type}).then(documents => documents[0]);

        if (!receiptDocument) {
            throw 'SALE.CANNOT_CANCEL_DOCUMENT';
        }

        if(!printer) {
            printer = (await this.entityManagerService.printers.fetchOneOffline(nonFiscalDocument.printer_id!)) || await this.entityManagerService.printers.fetchCollectionOffline({ type: 'receipt' }).then(printers => printers[0]);
        }

        if (!printer) {
            throw 'SALE.CANNOT_CANCEL_DOCUMENT';
        }

        const voidSale = await this.createReverseSale(targetSale, 'void_doc', refundCause, saleCustomer, { returnOnly: true });

        if (!voidSale) {
            return;
        }

        const documentConfig = {
            document_template: receiptDocument,
            document_type: { id: nonFiscalDocument?.document_type as PrinterDocumentType['id'] },
            printer: printer,
            reference_fiscal_document: refDocument,
            options: {}
        };

        const voidPromise = new Promise(async (resolve, reject) => {
            try {
                const { salePayments, error } = await this.salePaymentService.processPayments(voidSale, documentConfig);

                voidSale.payments = salePayments;

                if (error) {
                    throw error;
                }

                await this.documentPrinterService.printDocument(voidSale, documentConfig);

                this.closeVoidSale(voidSale);
                resolve(null);
            } catch (error) {
                reject({printError: error, printerId: printer.id});
            }
        });

        try {
            await this.waitDialogService.openDialog({ data: { message: 'HISTORY.SALE.DOCUMENT_CANCEL_IN_PROGRESS', promise: voidPromise } });
        } catch(error: any) {
            throw { printError: error.printError, printerId: printer.id };
        }

        setTimeout(() => gridRef?.refreshGrid(), 1100);
    }

    private async voidFiscalProviderDocument(targetSale: Sales, fiscalProviderDocument: SalesDocuments, printers: Printers[], refundCause: RefundCauses, saleCustomer?: SalesCustomer | string, gridRef?: GridServerSideComponent) {
        const fiscalProviderName = fiscalProviderDocument.meta?.fiscal_provider;
        const defPrinterId = parseInt(this.configurationManagerService.getPreference('fiscalprinter.def.id') || '0');

        //Use the default printer or find a printer with the fiscal provider that emitted the sale
        const fiscalProviderPrinter = (
            printers.find((printer) => printer.id === defPrinterId && printer.fiscal_provider === fiscalProviderName) ||
            printers.find((printer) => printer.type === 'receipt' && printer.fiscal_provider === fiscalProviderName)
        );

        if (!fiscalProviderPrinter) {
            throw 'SALE.PRINTER_NOT_AVAILABLE';
        }

        const fiscalProvider = this.FiscalProvidersService.getFiscalProvider(fiscalProviderName);

        if (!fiscalProvider) {
            throw 'SALE.INVALID_FISCAL_PROVIDER';
        }

        if (refundCause.id === 6) { //VOID
            if (typeof fiscalProvider.voidFiscalDocument !== 'function') {
                throw 'SALE.CANNOT_CANCEL_DOCUMENT';
            }

            await this.emitNonFiscalVoidSale(targetSale, refundCause, saleCustomer, fiscalProviderPrinter, fiscalProviderDocument, gridRef);
        } else {
            //TODO: stub implementation: this needs to be finished as soon as we have a fiscal provider that allows refunds
            if (typeof fiscalProvider.refundFiscalDocument !== 'function') {
                throw 'SALE.CANNOT_CANCEL_DOCUMENT';
            }

            this.createReverseSale(targetSale, 'refund_doc', refundCause, saleCustomer);
        }
    };

    private async voidRTCommercialDocument(targetSale: Sales, rtPrinters: Printers[], printerSerial: string, refundCause: RefundCauses, saleCustomer: SalesCustomer|string|undefined, gridRef?: GridServerSideComponent) {
        const waitSubject = new Subject<Partial<WaitDialogModel.State>>();

        const printPromise = new Promise(async (resolve, reject) => {
            try {
                //Query all RT printers to find which one printed the document
                let targetPrinter;

                for (const printer of rtPrinters) {
                    try {
                        const status = await this.FiscalPrintersService.getPrinterStatus(printer);

                        if (status.printer_serial === printerSerial) {
                            targetPrinter = printer;
                            break;
                        }
                    } catch (err) {
                        //Nothing to do
                    }
                }

                if (!targetPrinter) {
                    throw 'SALE.PRINTER_NOT_AVAILABLE';
                }

                const sale = await this.createReverseSale(targetSale, 'void_doc', refundCause, saleCustomer, { returnOnly: true });

                if (!sale) {
                    return;
                }

                waitSubject.next({ message: this.translate.instant('HISTORY.SALE.DOCUMENT_CANCEL_IN_PROGRESS') });

                try {
                    await this.documentPrinterService.printDocument(sale, {
                        printer: targetPrinter,
                        document_type: { id: 'fiscal_receipt' },
                        options: {}
                    });

                    this.closeVoidSale(sale);
                    resolve(null);
                } catch (err) {
                    reject({printError: err, printerId: targetPrinter.id});
                }
            } catch (err) {
                reject(err);
            }
        });

        await this.waitDialogService.openDialog({ data: { message: 'HISTORY.SALE.SEARCHING_PRINTER', promise: printPromise, stateSubject: waitSubject } });

        setTimeout(() => gridRef?.refreshGrid(), 1100);
    };

    private closeVoidSale(sale: Sales) {
        Object.assign(sale, {
            closed_at: new Date().toISOString(),
            status: 'closed'
        });

        this.entityManagerService.sales.postOneOfflineFirst(sale);
    };

    public async oldSummaryInvoiceDialog() {
        if(['FR'].includes(this.configurationManagerService.getShopCountry())) {
            this.alertDialogService.openDialog({data: {
                messageLabel: 'Il n’est pas possible de transformer un reçu en facture. Il faut annuler le reçu, puis générer la facture.'
            }});

            return;
        }

        let isSelectingClaimed = false;
        let isSelectingUnclaimed = false;

        // Check if all the sales are eligible for a summary sale
        try {

            for(const sale of this.selectedSales) {
                if(sale.final_amount! < 0) {
                    throw 'REFUND_PRESENT';
                }

                if(sale.is_summary) {
                    throw 'SUMMARY_SALE_PRESENT';
                }

                if(sale.status === 'stored') {
                    throw 'STORED_SALE_PRESENT';
                }

                if(sale.e_invoice) {
                    throw 'E_INVOICE_PRESENT';
                }

                const hasClaimedPayments = !!sale.payments?.some(p => !p.unclaimed);
                const hasUnclaimedPayments = !!sale.payments?.some(p => p.unclaimed);

                if(hasClaimedPayments && hasUnclaimedPayments) {
                    throw 'MIXED_PAYMENTS_PRESENT';
                }

                if(isSelectingClaimed && hasUnclaimedPayments) {
                    throw 'MIXED_PAYMENTS_PRESENT';
                }

                if(isSelectingUnclaimed && hasClaimedPayments) {
                    throw 'MIXED_PAYMENTS_PRESENT';
                }

                isSelectingClaimed ||= hasClaimedPayments;
                isSelectingUnclaimed ||= hasUnclaimedPayments;
            }
        } catch(err) {
            this.alertDialogService.openDialog({
                data: {
                    messageLabel: this.translate.instant('HISTORY.SALES.SUMMARY_SALE_CHECK.ERROR', { reason: this.translate.instant(`HISTORY.SALES.SUMMARY_SALE_CHECK.${err}`) })
                }
            });

            return;
        }

        const confirm = await this.confirmDialogService.openDialog({
            data: {
                messageLabel: 'TOPBAR.ACTIONS.SUMMARY_INVOICE_DIALOG'
            }
        });

        if(!confirm) {
            return;
        }

        const summaryType = isSelectingClaimed
            ? 'summary_e_nrc'
            : isSelectingUnclaimed
                ? 'summary_e_rc'
                : 'summary';

        await this.createSummarySale(this.selectedSales, summaryType);
    }

    async createSummarySale(sales: Sales[] = this.selectedSales, summaryType?: 'summary_e_rc' | 'summary_e_nrc' | 'summary') {
        if (!this.configurationManagerService.isModuleEnabled('cashregister')) {
            return this.alertDialogService.openDialog({data: {
                messageLabel: 'HISTORY.CASHREGISTER_MODULE_NOT_ENABLED'
            }});
        }

        if (this.configurationManagerService.isFunctionEnabledOptin('fiscalprinter.override_e_invoice_checks')) {
            let [option] = await this.optionsDialogService.show(this.translate.instant('HISTORY.SALES.CREATE_SUMMARY_INVOICE_TITLE'),[
                {
                    message: this.translate.instant('HISTORY.SALES.CREATE_SUMMARY_INVOICE_DESCRIPTION'),
                    options: [
                        { name: this.translate.instant('HISTORY.SALES.CREATE_SUMMARY_INVOICE_TRADITIONAL'), value: "summary" },
                        { name: this.translate.instant('HISTORY.SALES.CREATE_SUMMARY_INVOICE_ELECTRONIC'), value: summaryType }
                    ],
                    required: true
                }
            ]);

            if (option) {
                summaryType = option;
            }
        }

        const customersInSales: SalesCustomer[] = [];
        const newSaleItems: SalesItems[] = [];
        const priceChanges: SalesPriceChanges[] = [];

        const saleItemUuids = new Set<string>();

        for(const sale of sales) {
            const saleReference = this.getSaleReference(sale);

            const getReference = () => {
                const seqNum = saleReference.reference_sequential_number || saleReference.reference_text || '';
                const docType = (typeof seqNum === 'string') && /[0-9]{4}-[0-9]{4}/.test(seqNum) ? "DC" : "SC";

                return `${docType} n.${seqNum}`;
            }

            if (sale.sale_customer) {
                customersInSales.push(sale.sale_customer);
            }

            for(const saleItem of sale.sale_items || []) {
                const { notes, ...tmpSaleItem } = saleItem;

                // If the sale item uuid already exists, generate a new UUID
                if(saleItemUuids.has(tmpSaleItem.uuid)) {
                    tmpSaleItem.uuid = generateUuid();
                }

                newSaleItems.push({
                    ...structuredClone(tmpSaleItem),
                    ...saleReference
                });
                
                saleItemUuids.add(tmpSaleItem.uuid);
            }

            if(sale.price_changes?.length) {
                for(const priceChange of sale.price_changes) {
                    const pcAmount = priceChange.amount;

                    if (pcAmount) {
                        const isDiscount = priceChange.type.startsWith('discount');
                        const saleDiscountLabel = isDiscount ? 'SUMMARY_SALE_DISCOUNT' : 'SUMMARY_SALE_SURCHARGE';
                        const pcType = isDiscount ? 'discount_fix' : 'surcharge_fix';

                        priceChanges.push({
                            amount: pcAmount,
                            index: priceChanges.length + 1,
                            type: pcType,
                            value: Math.abs(pcAmount),
                            description: this.translate.instant(`HISTORY.SALES.${saleDiscountLabel}`, { sale: getReference() })
                        });
                    }
                }
            }
        }

        const customersInSalesWithName: { name: string, customer: SalesCustomer | null, id?: string }[] = Array.from(new Map(customersInSales.map(customer => [customer.customer_id, customer])).values())
            .map(customer => ({
                // uuid is optional: the option needs an id of its own
                id: customer.uuid ?? String(customer.customer_id),
                name: CustomerUtilsService.getCustomerName(customer) || '-',
                customer: customer
            }));

        let selectedCustomer;

        switch (customersInSalesWithName.length) {
            case 0:
                break;
            case 1:
                selectedCustomer = customersInSalesWithName[0].customer;
                break;
            default:
                customersInSalesWithName.unshift({
                    id: 'none',
                    name: this.translate.instant('HISTORY.CUSTOMER_SELECT.NO_CLIENT'),
                    customer: null
                });

                const res = await this.listSelectorDialogService.openDialog<typeof customersInSalesWithName[number]>({
                    data: {
                        optionList: customersInSalesWithName,
                        title: 'HISTORY.CUSTOMER_SELECT.TITLE',
                        searchable: false
                    }
                });

                if(!res) {
                    return;
                }

                selectedCustomer = res.customer;

                break;
        }

        await this.historyUtilsService.createSale({
            sale_items: newSaleItems,
            sale_customer: selectedCustomer || undefined,
            price_changes: priceChanges
        }, {
            saleType: summaryType!
        });
    };

    getSaleReference(sale: Sales): SaleReference {
        const saleReference: SaleReference = {
            reference_sale_id: sale.id!
        };

        if (sale.sale_documents) {
            const saleReceipt = sale.sale_documents.find((saleDocument) => ['fiscal_receipt', 'commercial_doc', 'fiscal_provider'].includes(saleDocument.document_type));

            if (saleReceipt && Number.isFinite(saleReceipt.sequential_number) && saleReceipt.date) {
                switch (saleReceipt.document_type) {
                    case 'fiscal_receipt':
                        Object.assign(saleReference, {
                            reference_sequential_number: saleReceipt.sequential_number,
                            reference_date: saleReceipt.date
                        });
                    break;
                    case 'commercial_doc':
                        const seqNumStr = `${saleReceipt.sequential_number}`.padStart(8, '0');
                        const refText = [seqNumStr.substring(0, 4), seqNumStr.substring(4, 8)];

                        Object.assign(saleReference, {
                            reference_text: refText.join('-'),
                            reference_date: saleReceipt.date
                        });
                    break;
                    case 'fiscal_provider':
                        const fiscalProvider = this.FiscalProvidersService.getFiscalProvider(saleReceipt.meta?.fiscal_provider);

                        if (fiscalProvider.getSaleReference) {
                            return fiscalProvider.getSaleReference(sale);
                        }

                        Object.assign(saleReference, {
                            reference_sequential_number: saleReceipt.sequential_number,
                            reference_date: saleReceipt.date
                        });
                    break;
                    default:
                        break;
                }
            }
        }

        return saleReference;
    };

    /**
     * DUPLICATE SALE FROM SALE CLOSED
     * @param sale
     */
    public async duplicateSale(sale: Sales) {
        const res = await this.confirmDialogService.openDialog({
            data: {
                messageLabel: 'DIALOG.DUPLICATE_SALE.TITLE',
                confirmLabel: 'APPLICATION.YES',
                cancelLabel: 'APPLICATION.NO'
            }
        });

        if(!res) {
            return;
        }

        const {
            sale_items,
            sale_customer,
            price_changes,
            notes
        } = structuredClone(sale);

        this.historyUtilsService.createSale({
            sale_items,
            sale_customer,
            price_changes,
            notes,
        }, {
            regenerateUuids: true
        });
    }

    public async restoreSale(sale: Sales, gridRef?: GridServerSideComponent) {
        const res = await this.confirmDialogService.openDialog({
            data: {
                messageLabel: 'DIALOG.RESTORE_SALE.TITLE',
                confirmLabel: 'APPLICATION.YES',
                cancelLabel: 'APPLICATION.NO'
            }
        });

        if (!res) {
            return;
        }

        try {
            switch (sale.status) {
                case 'closed':
                    const voidSale = await this.doRefundVoid(sale, { refundCauseId: 6, gridRef });

                    if (voidSale) {
                        const defPrinterId = this.configurationManagerService.getPreference('fiscalprinter.def.id');
                        const defaultDocumentId = <PrinterDocumentTypeId>this.configurationManagerService.getPreference('fiscalprinter.def.document_type');

                        if (!defPrinterId || !defaultDocumentId) {
                            throw 'HISTORY.SALE.NO_DEFAULT_PRINTER';
                        }

                        const voidPromise = this.saleBatchPrintService.printSale(voidSale, parseInt(defPrinterId), defaultDocumentId, { allowRefunds: true });
                        await this.waitDialogService.openDialog({ data: { message: 'HISTORY.SALE.DOCUMENT_CANCEL_IN_PROGRESS', promise: voidPromise } });
                    }

                    const {
                        sale_items,
                        sale_customer,
                        price_changes,
                        notes,
                        e_invoice,
                        table_id,
                        table_name,
                        room_id,
                        room_name
                    } = structuredClone(sale);

                    await this.historyUtilsService.createSale({
                        sale_items,
                        sale_customer,
                        price_changes,
                        notes,
                        e_invoice,
                        overrides: {
                            table_id,
                            table_name,
                            room_id,
                            room_name
                        }
                    }, {
                        saleType: this.getEmittedDocument(sale)?.document_type || 'fiscal_receipt',
                        regenerateUuids: true
                    });

                    break;
                case 'stored':
                    // Reopen stored sale
                    const reopenedSale = await this.entityManagerService.sales.putOneOnline({ ...sale, status: 'open', closed_at: undefined }, undefined, { force_update: true });
                    // Fetch transactions for this sale
                    await this.entityManagerService.saleTransactions.fetchCollectionOnline({ sale_uuid: reopenedSale.uuid, pagination: false }, true);
                    // Go to cashregister
                    this.historyUtilsService.openSaleInCashregister(reopenedSale.uuid!);
            }
        } catch (error: any) {
            this.showError(error);
        }
    }

    async deleteSalesMassive(sales: Array<Sales>) {
        const num_sales = sales.length;
        const question = this.translate.instant('HISTORY.DELETE_MASSIVE_SALES.QUESTION', {num_sales} );
        const confirm = await this.confirmDialogService.openDialog({
            data: {
                messageLabel: question,
                confirmLabel: 'APPLICATION.YES',
                cancelLabel: 'APPLICATION.NO'
            }
        });

        if (confirm) {
            const titleParams = { num_sales: num_sales.toString(), digitsToEnter: 'Elimina vendite' };
            const answer = await this.promptDialogService.openDialog({
                data: {
                    disableCancel: false,
                    title: 'HISTORY.DELETE_MASSIVE_SALES.TITLE',
                    titleParams,
                    subtitle: 'HISTORY.DELETE_MASSIVE_SALES.SUBTITLE',
                    label: 'HISTORY.DELETE_MASSIVE_SALES.LABEL',
                    type: "text",
                    pattern: "^Elimina vendite$"
                }
            });

            if(answer === 'Elimina vendite') {
                // Avvia caricamento
                this.loadingSubject.next(true);
                this.progressSubject.next(0);

                let returnValue = { message: 'error', errors: [] as Array<any> };

                try {
                    const sale_ids = sales.map((sale: Sales) => sale.id).filter((id): id is number => id !== undefined);
                    const chunkSize = 100; // Massimo numero di elementi per ogni chiamata
                    const parallelLimit = 5; // Massimo numero di chiamate parallele

                    // 1. Funzione per creare i chunks
                    const chunks = chunk(sale_ids, chunkSize);
                    let completed = 0;

                    // 2. Funzione per gestire il singolo chunk con retry
                    const processChunk = async (chunk: Array<number>, index: number): Promise<any | null> => {
                        const maxRetries = 3;
                        let attempts = 0;
                        while (attempts < maxRetries) {
                            try {
                                await this.restManagerService.post('massive_delete/sales', { sale_ids: chunk });

                                // Aggiorna progresso per la UI
                                completed++;
                                this.progressSubject.next(Math.round((completed / chunks.length) * 100));

                                return null; // Successo: nessun errore da restituire
                            } catch (error) {
                                attempts++;
                                if (attempts >= maxRetries) return error; // Fallimento definitivo
                                new Promise(res => setTimeout(res, 2000)); // Attesa breve tra i retry
                            }
                        }
                    };

                    // 3. Esecuzione con LIMITE DI CONCORRENZA
                    const waitPromise = await this.waitDialogService.openDialog({ data: {
                        message: 'HISTORY.DELETE_MASSIVE_SALES.LOADING',
                        promise: asyncParallelLimit(chunks, processChunk, { parallelLimit: parallelLimit, abortOnError: false })
                    }});

                    // 4. Gestione dei risultati
                    const results: Array<Promise<any>> = waitPromise.results;
                    const errors: Array<any> = [];

                    for (let i = 0; i < results.length; i++) {
                        const result = await results[i];

                        if(result) {
                            errors.push(result);
                        }
                    }

                    returnValue = { message: errors.length === 0 ? 'success' : 'error', errors };
                } catch (error: any) {
                    returnValue = { message: 'error', errors: [error] };
                } finally {
                    // Chiudi caricamento
                    this.loadingSubject.next(false);
                }

                return returnValue;
            }
        }
    }
}
